import { LocationAnchorSource, useHotelAllLocationAnchorsDeprecatedQuery, useHotelAllLocationAnchorsQuery } from '@graphql/generated';
import { HotelSearchParamsType, HotelSearchQueryParams } from './HotelSearch.types';
import { useQueryParamHelper } from '@shared/core/queryString';
import { addError } from '@shared/utils/logger';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useClientReference } from '@shared/hooks/useClientReference';
import { useCheckInCheckoutDates } from '@shared/hooks/useCheckInCheckoutDates';

export const useHotelSearchParams = (eventHandle?: string) => {
  const queryParamHelper = useQueryParamHelper();

  // Utility function for retrieving query params
  const getQueryParam = (param: HotelSearchQueryParams) => queryParamHelper.getValueString(param);

  // Retrieve query parameters
  const queryParamPlaceId = getQueryParam(HotelSearchQueryParams.placeId);
  const queryParamName = getQueryParam(HotelSearchQueryParams.name);
  const queryParamCheckInDate = getQueryParam(HotelSearchQueryParams.checkInDate);
  const queryParamCheckOutDate = getQueryParam(HotelSearchQueryParams.checkOutDate);
  const queryParamEventId = getQueryParam(HotelSearchQueryParams.eventId);
  const utmParams = {
    utm_source: getQueryParam(HotelSearchQueryParams.utm_source),
    utm_source_secondary: getQueryParam(HotelSearchQueryParams.utm_source_secondary),
    utm_medium: getQueryParam(HotelSearchQueryParams.utm_medium),
    utm_campaign: getQueryParam(HotelSearchQueryParams.utm_campaign)
  };

  // Don't need to fetch data, If don't have eventHandle OR already have placeId, checkin & checkout in query param.
  const shouldSkipQuery = !eventHandle || Boolean(queryParamPlaceId && queryParamCheckInDate && queryParamCheckOutDate);
  // Fetch location data via GraphQL
  const { data, loading: loadingData, error: locationsError } = useHotelAllLocationAnchorsQuery({
    variables: { name: eventHandle! },
    batchMode: 'fast',
    skip: shouldSkipQuery
  });

  // Don't need to fetch data, If don't have eventId OR already have placeId, checkin & checkout in query param.
  const shouldSkipQueryDeprecated = !queryParamEventId || Boolean(queryParamPlaceId && queryParamCheckInDate && queryParamCheckOutDate);
  // @todo - Cleanup with ticket - https://withjoy.atlassian.net/browse/TRAV-622
  const { data: dataDeprecated, loading: loadingDataDeprecated, error: locationsErrorDeprecated } = useHotelAllLocationAnchorsDeprecatedQuery({
    variables: { eventByIdId: queryParamEventId! },
    batchMode: 'fast',
    skip: shouldSkipQueryDeprecated
  });

  const eventDate = eventHandle ? data?.eventByName?.info.finalizedDate?.dateString : dataDeprecated?.eventById?.info.finalizedDate?.dateString;
  const { checkIn: eventDateCheckIn, checkOut: eventDateCheckOut, message: checkInCheckOutMessage } = useCheckInCheckoutDates({ eventDate });

  // Generate unique client reference
  const { clientReference, loadingClientReference } = useClientReference(queryParamEventId);

  // Default values for location
  let placeId = '';
  let name = '';
  let locationSource = '';

  // Get location anchors
  const locationAnchors = eventHandle ? data?.eventByName?.allLocationAnchors ?? [] : dataDeprecated?.eventById?.allLocationAnchors ?? [];
  const venueLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.event_venue);
  const centroidLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.approximated_centroid);
  const eventLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.event_location);

  // Experiment logic for location selection
  const { value, loading: loadingExperiment } = useFeatureValue('hotelSearchLocationExperiment', {
    skip: !!venueLocation || !(centroidLocation && eventLocation)
  });

  const hotelSearchLocationExperimentEnabled = value === 'treatment';

  let locationMessage = 'Location: ';

  const checkin = queryParamCheckInDate || eventDateCheckIn;
  const checkout = queryParamCheckOutDate || eventDateCheckOut;

  // Determine location selection
  if (queryParamPlaceId) {
    placeId = queryParamPlaceId;
    name = queryParamName || '';
    locationSource = 'queryParam';
    locationMessage += `Found queryParamPlaceId - `;
  } else if (venueLocation) {
    placeId = venueLocation.googlePlaceID;
    name = venueLocation.friendlyName;
    locationSource = venueLocation.source;
    locationMessage += `Found venue - `;
  } else if (centroidLocation && eventLocation) {
    locationMessage += `Not found venue, used experiment - `;
    if (hotelSearchLocationExperimentEnabled) {
      placeId = centroidLocation.googlePlaceID;
      name = centroidLocation.friendlyName;
      locationSource = centroidLocation.source;
    } else {
      placeId = eventLocation.googlePlaceID;
      name = eventLocation.friendlyName;
      locationSource = eventLocation.source;
    }
  } else if (centroidLocation || eventLocation) {
    const location = centroidLocation ?? eventLocation;
    placeId = location?.googlePlaceID!;
    name = location?.friendlyName!;
    locationSource = location?.source!;
    locationMessage += `Not found venue, No experiment - `;
  } else {
    locationMessage += `Not found venue, No experiment, No Location.`;
  }

  locationMessage += `placeId: ${placeId}, name: ${name}, locationSource: ${locationSource}`;

  // Prepare the search params object
  const params: HotelSearchParamsType = {
    clientReference,
    placeId,
    locationSource,
    name,
    checkin,
    checkout,
    ...utmParams
  };

  // Function to build redirect URL
  const buildRedirectUrl = ({ url, fields }: { url: string; fields: (keyof HotelSearchParamsType)[] }) => {
    // eslint-disable-next-line no-console
    console.log(checkInCheckOutMessage);
    // eslint-disable-next-line no-console
    console.log(locationMessage);

    // Error handling
    const errors = [
      { condition: locationsError || locationsErrorDeprecated, message: 'HotelSearch - GraphQL query failed to fetch AllLocationAnchors data' },
      { condition: !queryParamEventId && !eventHandle, message: 'HotelSearch - eventId and eventHandle Missing' },
      { condition: !placeId, message: 'HotelSearch - PlaceId Missing' },
      { condition: !name, message: 'HotelSearch - Name Missing' },
      { condition: !utmParams.utm_source, message: 'HotelSearch - Source Missing' }
    ];

    errors.forEach(({ condition, message }) => condition && addError(message, { context: 'HotelSearch' }));

    const redirectUrl = new URL(url);
    fields.forEach(field => {
      const fieldValue = params[field];
      if (fieldValue != null) {
        redirectUrl.searchParams.append(field, fieldValue.toString());
      }
    });

    return redirectUrl;
  };

  return { ...params, loading: loadingData || loadingDataDeprecated || loadingExperiment || loadingClientReference, buildRedirectUrl };
};
